import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Error, CustomHttpErrorResponse } from '../../../../../../common/constants/error';
import { LIST_SERVER_PAGINATION_INITIAL_STATE } from '../../../../../../common/models/list.model';
import { ListServerPaginationStateModel, NotificationService } from '../../../../../../common';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, take } from 'rxjs/operators';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { YardSpotConfigurationService } from './yard-spot-configuration.service';
import {
  YardSpotDelete,
  YardSpotListInitialState,
  YardSpotListLoad,
  YardSpotSave,
} from './yard-spot-configuration.actions';
import { YardSpotConfigurationModel } from '../../models';
import { ListSearchCriteriaFacade, ListSearchCriteriaType } from '../../../../list-search-criteria';

export type YardSpotConfigurationStateModel = ListServerPaginationStateModel<YardSpotConfigurationModel> & {
  isDeleting: boolean;
};

const initialState: YardSpotConfigurationStateModel = { ...LIST_SERVER_PAGINATION_INITIAL_STATE, isDeleting: false };

@State<YardSpotConfigurationStateModel>({
  name: 'yardSpotList',
  defaults: initialState,
})
@Injectable()
export class YardSpotConfigurationState {
  constructor(
    private yardSpotConfigurationService: YardSpotConfigurationService,
    private notificationService: NotificationService,
    private listSearchCriteriaFacade: ListSearchCriteriaFacade,
  ) {}

  @Selector()
  static getYardSpots(
    state: YardSpotConfigurationStateModel,
  ): ListServerPaginationStateModel<YardSpotConfigurationModel> {
    return state;
  }

  @Selector()
  static getLoaded(state: YardSpotConfigurationStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static isDeleting(state: YardSpotConfigurationStateModel): boolean {
    return state.isDeleting;
  }

  @Action(YardSpotSave)
  saveYardSpot(
    ctx: StateContext<YardSpotConfigurationStateModel>,
    { payload }: YardSpotSave,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.yardSpotConfigurationService.createYardSpot(payload?.data).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.yardSpot.success.save');
        this.updateList(ctx);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(YardSpotListLoad)
  getYardSpots(ctx: StateContext<YardSpotConfigurationStateModel>): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.yardSpotConfigurationService.getYardSpotListData().pipe(
      take(1),
      map((data: YardSpotConfigurationModel[]) => {
        ctx.patchState({
          items: data,
          totalElements: data.length,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(YardSpotDelete)
  deleteYardSpot(
    ctx: StateContext<YardSpotConfigurationStateModel>,
    { payload: { dialog, id } }: YardSpotDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ isDeleting: true });

    return this.yardSpotConfigurationService.deleteYardSpot(id).pipe(
      map(() => {
        dialog.close();
        this.notificationService.success('api.yardSpot.success.delete');
        this.updateList(ctx);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
      finalize(() => ctx.patchState({ isDeleting: false })),
    );
  }

  @Action(YardSpotListInitialState)
  setInitialState(ctx: StateContext<YardSpotConfigurationStateModel>): void {
    ctx.setState(initialState);
  }

  private updateList(ctx: StateContext<YardSpotConfigurationStateModel>): void {
    this.listSearchCriteriaFacade
      .getSearchCriteriaByListType(ListSearchCriteriaType.YardSpots)
      .subscribe(() => ctx.dispatch(new YardSpotListLoad()));
  }
}
